import type { AuthProvider } from "@refinedev/core";
import type { Session, SupabaseClient, User } from "@supabase/supabase-js";

import { fetchTrustedStaffSession, resolveTrustedStaffSession } from "../api/staff-session";
import { getAdminPublicConfig } from "../config/public-config";
import { markStaffAccessDenied } from "./access-denied-notice";
import { markRecoveryRequested } from "./password-recovery";
import { createAdminSupabaseClient } from "./supabase";

type AuthClient = Pick<SupabaseClient, "auth">;

type AdminAuthDependencies = Readonly<{
  client?: AuthClient | null;
  fetcher?: typeof fetch;
}>;

function errorResponse(name: string, message: string) {
  return { success: false, error: { name, message } };
}

type TrustedSessionState =
  | Readonly<{
      status: "ok";
      session: Session;
      user: User;
      staff: NonNullable<Awaited<ReturnType<typeof fetchTrustedStaffSession>>>;
    }>
  | Readonly<{ status: "denied"; httpStatus?: number }>
  | Readonly<{ status: "unavailable" }>;

async function trustedSessionState(
  client: AuthClient,
  dependencies: AdminAuthDependencies,
): Promise<TrustedSessionState> {
  const { data, error } = await client.auth.getSession();
  if (error || !data.session) return { status: "denied" };
  const { data: userData, error: userError } = await client.auth.getUser(data.session.access_token);
  if (userError || !userData.user) return { status: "denied" };
  const config = getAdminPublicConfig();
  const outcome = await resolveTrustedStaffSession(
    config.apiUrl,
    data.session.access_token,
    dependencies.fetcher,
  );
  if (outcome.status !== "ok") return outcome;
  return { status: "ok", session: data.session, user: userData.user, staff: outcome.session };
}

async function trustedSession(client: AuthClient, dependencies: AdminAuthDependencies) {
  const state = await trustedSessionState(client, dependencies);
  return state.status === "ok" ? state : null;
}

export function createSupabaseAuthProvider(dependencies: AdminAuthDependencies = {}): AuthProvider {
  const getClient = () => dependencies.client ?? createAdminSupabaseClient();

  return {
    login: async (params: { email?: string; password?: string; mfaCode?: string }) => {
      const client = getClient();
      if (!client)
        return errorResponse("AuthNotConfigured", "Supabase authentication is not configured.");
      if (!params.email || !params.password) {
        return errorResponse("InvalidCredentials", "Email and password are required.");
      }
      const { data, error } = await client.auth.signInWithPassword({
        email: params.email,
        password: params.password,
      });
      if (error || !data.session) {
        return errorResponse("SignInFailed", "The email or password was not accepted.");
      }
      let session = data.session;
      const config = getAdminPublicConfig();
      const { data: assurance, error: assuranceError } =
        await client.auth.mfa.getAuthenticatorAssuranceLevel(session.access_token);
      if (assuranceError) {
        await client.auth.signOut();
        return errorResponse(
          "MfaCheckFailed",
          "Unable to verify the staff account assurance level.",
        );
      }
      // Production always asks for the MFA code. Staging asks too when the API runs with LAFAYETTE_REQUIRE_STAFF_AAL2=true (CHT-1628).
      const asksForMfa = config.environment === "production" || config.requireStaffAal2 === true;
      if (asksForMfa && assurance.currentLevel !== "aal2") {
        const { data: factors, error: factorsError } = await client.auth.mfa.listFactors();
        const factor = factors?.totp.find((candidate) => candidate.status === "verified");
        if (factorsError || !factor || !params.mfaCode) {
          await client.auth.signOut().catch(() => undefined);
          return errorResponse("MfaRequired", "A six-digit MFA code is required for staff access.");
        }
        const { error: verifyError } = await client.auth.mfa.challengeAndVerify({
          factorId: factor.id,
          code: params.mfaCode,
        });
        if (verifyError) {
          await client.auth.signOut().catch(() => undefined);
          return errorResponse("MfaFailed", "The MFA code was not accepted.");
        }
        const refreshed = await client.auth.getSession();
        if (refreshed.error || !refreshed.data.session) {
          await client.auth.signOut().catch(() => undefined);
          return errorResponse("MfaFailed", "The MFA session could not be established.");
        }
        session = refreshed.data.session;
      }
      const staff = await fetchTrustedStaffSession(
        config.apiUrl,
        session.access_token,
        dependencies.fetcher,
      );
      if (!staff) {
        await client.auth.signOut();
        return errorResponse(
          "StaffAccessDenied",
          "This account is not authorized for the staff console.",
        );
      }
      return { success: true, redirectTo: "/home" };
    },
    logout: async () => {
      const client = getClient();
      // Global on purpose (CHT-1956): the staff sign-out ends every session of the account.
      if (client) await client.auth.signOut({ scope: "global" });
      return { success: true, redirectTo: "/login" };
    },
    check: async () => {
      const client = getClient();
      if (!client) return { authenticated: false, redirectTo: "/login" };
      const current = await trustedSessionState(client, dependencies);
      if (current.status === "unavailable") {
        // A transient API outage must not destroy a valid session; only an explicit denial does.
        return {
          authenticated: false,
          error: new Error("The staff session could not be verified right now. Try again shortly."),
        };
      }
      if (current.status === "denied") {
        // A 403 is a valid session that is not staff (a customer on the admin origin): say why they are signed out. An expired
        // or unknown session (401, or no Supabase session at all) gets the plain sign-in page.
        if (current.httpStatus === 403) markStaffAccessDenied(window.localStorage);
        await client.auth.signOut().catch(() => undefined);
        return { authenticated: false, logout: true, redirectTo: "/login" };
      }
      return { authenticated: true };
    },
    forgotPassword: async ({ email }: { email?: string }) => {
      const client = getClient();
      if (!client)
        return errorResponse("AuthNotConfigured", "Supabase authentication is not configured.");
      if (!email) return errorResponse("InvalidEmail", "Email is required.");
      const { error } = await client.auth.resetPasswordForEmail(email, {
        redirectTo: getAdminPublicConfig().authRedirectUrl,
      });
      if (error) return errorResponse("ResetFailed", "Unable to start the password reset flow.");
      markRecoveryRequested(window.localStorage);
      return { success: true };
    },
    getPermissions: async () => {
      const client = getClient();
      if (!client) return [];
      const current = await trustedSession(client, dependencies);
      return current?.staff.roles ?? [];
    },
    getIdentity: async () => {
      const client = getClient();
      if (!client) return null;
      const current = await trustedSession(client, dependencies);
      if (!current) return null;
      return {
        id: current.user.id,
        name: current.user.email ?? current.user.id,
        email: current.user.email,
        roles: current.staff.roles,
        mfaSatisfied: current.staff.mfaSatisfied,
      };
    },
    onError: async (error: { statusCode?: number }) => {
      if (error.statusCode === 401 || error.statusCode === 403) {
        return { logout: true, redirectTo: "/login" };
      }
      return {};
    },
  };
}

export const supabaseAuthProvider = createSupabaseAuthProvider();
